import * as ex from 'excalibur';
import { CAMERA_SPEED, GRID_WIDTH, GRID_HEIGHT, TILE_SIZE, WORLD_WIDTH, WORLD_HEIGHT, MOVEMENT_SPEEDS } from '../config/constants';
import { Entity } from './entity';
import { Animal } from './animal';
import { Plant } from './plant';
import { EmotionSystem } from './EmotionSystem';

export class CameraController {
  public camera: ex.Camera;
  private engine: ex.Engine;
  private isPanning: boolean = false;
  private boundaryPadding: number = 50; // Padding to prevent showing outside the world
  private lastPointerPos: ex.Vector = null;
  private isDragging: boolean = false;
  public followingEntity: Entity | null = null;
  private keyboardPanSpeed: number = CAMERA_SPEED;
  
  // UI elements for the inspect panel
  private inspectPanel: HTMLElement | null = null;
  private panelTitle: HTMLElement | null = null;
  private animalType: HTMLElement | null = null;
  private animalPosition: HTMLElement | null = null;
  private animalBehaviors: HTMLElement | null = null;
  private animalSpeed: HTMLElement | null = null;
  private animalPrey: HTMLElement | null = null;
  private animalPredators: HTMLElement | null = null;
  private preyRow: HTMLElement | null = null;
  private predatorRow: HTMLElement | null = null;
  private stopFollowingButton: HTMLElement | null = null;
  
  // Reaction and metabolism sections
  private reactionsSection: HTMLElement | null = null;
  private metabolismSection: HTMLElement | null = null;

  constructor(engine: ex.Engine) {
    this.engine = engine;
    this.camera = engine.currentScene.camera;
    
    // Initialize camera to center of world
    this.camera.pos = new ex.Vector(WORLD_WIDTH / 2, WORLD_HEIGHT / 2);
    
    // Get UI elements
    this.inspectPanel = document.getElementById('inspect-panel');
    this.panelTitle = document.getElementById('panel-title');
    this.animalType = document.getElementById('animal-type');
    this.animalPosition = document.getElementById('animal-position');
    this.animalBehaviors = document.getElementById('animal-behaviors');
    this.animalSpeed = document.getElementById('animal-speed');
    this.animalPrey = document.getElementById('animal-prey');
    this.animalPredators = document.getElementById('animal-predators');
    this.preyRow = document.getElementById('prey-row');
    this.predatorRow = document.getElementById('predator-row');
    this.stopFollowingButton = document.getElementById('stop-following-btn');
    
    // Get reaction and metabolism sections
    this.reactionsSection = document.getElementById('reactions-section');
    this.metabolismSection = document.getElementById('metabolism-section');
    
    // Set up the stop following button
    if (this.stopFollowingButton) {
      this.stopFollowingButton.addEventListener('click', () => {
        this.stopFollowingEntity();
      });
    }
    
    // Set up input handlers
    this.setupInputHandlers();
  }

  private setupInputHandlers() {
    // Handle keyboard input for camera movement
    this.engine.input.keyboard.on('hold', (evt: ex.KeyEvent) => {
      // Stop following entity when manually moving camera with keyboard
      if (this.followingEntity) {
        this.stopFollowingEntity();
      }
      
      // Handle arrow keys
      switch (evt.key) {
        case ex.Keys.ArrowUp:
        case ex.Keys.W:
          this.panCamera(0, -1);
          break;
        case ex.Keys.ArrowDown:
        case ex.Keys.S:
          this.panCamera(0, 1);
          break;
        case ex.Keys.ArrowLeft:
        case ex.Keys.A:
          this.panCamera(-1, 0);
          break;
        case ex.Keys.ArrowRight:
        case ex.Keys.D:
          this.panCamera(1, 0);
          break;
      }
    });

    // Handle pointer down to start drag - with space + left click
    this.engine.input.pointers.primary.on('down', (evt: ex.PointerEvent) => {
      // Only start dragging with space key + left click
      if (this.engine.input.keyboard.isHeld(ex.Keys.Space) && evt.button === ex.Input.PointerButton.Left) {
        this.lastPointerPos = evt.screenPos.clone(); // Use screenPos for consistency
        this.isDragging = true;
        console.log("Starting camera drag with space + left click");
        
        // Stop following any entity when manually dragging
        if (this.followingEntity) {
          this.stopFollowingEntity();
        }
      }
    });

    // Handle pointer move for dragging
    this.engine.input.pointers.primary.on('move', (evt: ex.PointerEvent) => {
      if (this.isDragging && this.lastPointerPos) {
        // Calculate drag delta in screen space (invert for intuitive dragging)
        const dragX = (this.lastPointerPos.x - evt.screenPos.x);
        const dragY = (this.lastPointerPos.y - evt.screenPos.y);
        
        // Apply drag movement to camera (with a drag sensitivity factor)
        this.moveCamera(dragX, dragY);
        
        // Update last position
        this.lastPointerPos = evt.screenPos.clone();
      }
    });
    
    // Handle pointer up to end drag
    this.engine.input.pointers.primary.on('up', () => {
      this.isDragging = false;
      this.lastPointerPos = null;
    });
  }

  // Start following an entity with the camera
  public followEntity(entity: Entity) {
    // If we're already following an entity, clear its highlight first
    if (this.followingEntity) {
      this.followingEntity.clearFollowHighlight();
    }
    
    this.followingEntity = entity;
    console.log(`Now following entity at (${entity.gridX}, ${entity.gridY})`);
    
    // Apply the highlight to the new entity
    entity.highlightAsFollowed();
    
    // Show and update the inspect panel for any entity type
    this.updateInspectPanel(entity);
    this.showInspectPanel();
  }

  // Stop following the current entity
  public stopFollowingEntity() {
    if (this.followingEntity) {
      console.log("Stopped following entity");
      
      // Clear the highlight on the entity
      this.followingEntity.clearFollowHighlight();
      this.followingEntity = null;
      
      // Hide the inspect panel
      this.hideInspectPanel();
    }
  }
  
  // Show the inspect panel
  private showInspectPanel() {
    if (this.inspectPanel) {
      this.inspectPanel.style.display = 'block';
    }
  }
  
  // Hide the inspect panel
  private hideInspectPanel() {
    if (this.inspectPanel) {
      this.inspectPanel.style.display = 'none';
    }
    
    // Hide reactions and metabolism sections
    if (this.reactionsSection) {
      this.reactionsSection.style.display = 'none';
    }
    
    if (this.metabolismSection) {
      this.metabolismSection.style.display = 'none';
    }
  }
  
  // Update the inspect panel with entity details
  private updateInspectPanel(entity: Entity) {
    if (!this.inspectPanel) return;
    
    // Common updates for any entity type
    
    // Update position
    if (this.animalPosition) {
      this.animalPosition.textContent = `X: ${entity.gridX}, Y: ${entity.gridY}`;
    }
    
    // Hide rows that might not apply to all entity types
    if (this.preyRow) this.preyRow.style.display = 'none';
    if (this.predatorRow) this.predatorRow.style.display = 'none';
    
    // Hide reactions and metabolism sections by default
    if (this.reactionsSection) {
      this.reactionsSection.style.display = 'none';
    }
    if (this.metabolismSection) {
      this.metabolismSection.style.display = 'none';
    }
    
    // Specific updates based on entity type
    if (entity instanceof Animal) {
      // Animal-specific updates
      
      // Update panel title
      if (this.panelTitle) {
        this.panelTitle.textContent = `${entity.animalType} Details`;
      }
      
      // Update animal type
      if (this.animalType) {
        this.animalType.textContent = entity.animalType;
      }
      
      // Update behaviors
      if (this.animalBehaviors) {
        this.animalBehaviors.textContent = entity.behaviors.join(', ');
      }
      
      // Update speed
      if (this.animalSpeed) {
        // Convert numeric speed to readable label
        let speedLabel = 'Normal';
        if (entity.moveSpeed === MOVEMENT_SPEEDS.VERY_SLOW) speedLabel = 'Very Slow';
        else if (entity.moveSpeed === MOVEMENT_SPEEDS.SLOW) speedLabel = 'Slow';
        else if (entity.moveSpeed === MOVEMENT_SPEEDS.NORMAL) speedLabel = 'Normal';
        else if (entity.moveSpeed === MOVEMENT_SPEEDS.FAST) speedLabel = 'Fast';
        else if (entity.moveSpeed === MOVEMENT_SPEEDS.VERY_FAST) speedLabel = 'Very Fast';
        
        this.animalSpeed.textContent = speedLabel;
      }
      
      // Update prey info
      if (this.preyRow && this.animalPrey) {
        if (entity.prey && entity.prey.length > 0) {
          this.preyRow.style.display = 'flex';
          this.animalPrey.textContent = entity.prey.join(', ');
        } else {
          this.preyRow.style.display = 'none';
        }
      }
      
      // Update predator info
      if (this.predatorRow && this.animalPredators) {
        if (entity.predators && entity.predators.length > 0) {
          this.predatorRow.style.display = 'flex';
          this.animalPredators.textContent = entity.predators.join(', ');
        } else {
          this.predatorRow.style.display = 'none';
        }
      }
      
      // Show and update reactions section for animals
      if (this.reactionsSection) {
        this.reactionsSection.style.display = 'block';
        this.updateReactionSection(entity);
      }
      
      // Show and update metabolism section for animals
      if (this.metabolismSection) {
        this.metabolismSection.style.display = 'block';
        this.updateMetabolismSection(entity);
      }
    } else if (entity instanceof Plant) {
      // Plant-specific updates
      
      // Update panel title
      if (this.panelTitle) {
        this.panelTitle.textContent = `${entity.plantType} Details`;
      }
      
      // Update type field
      if (this.animalType) {
        this.animalType.textContent = entity.plantType;
      }
      
      // Update behaviors field to show growth info
      if (this.animalBehaviors) {
        this.animalBehaviors.textContent = `Growth: ${entity.growthStage}/${entity.maxGrowthStage}`;
      }
      
      // Update speed field to show growth rate
      if (this.animalSpeed) {
        const growthRateInSeconds = Math.round(entity.growthRate / 1000);
        this.animalSpeed.textContent = `${growthRateInSeconds} seconds`;
      }
    } else {
      // Generic entity fallback
      
      // Update panel title
      if (this.panelTitle) {
        this.panelTitle.textContent = `Entity Details`;
      }
      
      // Update type field
      if (this.animalType) {
        this.animalType.textContent = entity.type;
      }
      
      // Clear other fields
      if (this.animalBehaviors) {
        this.animalBehaviors.textContent = '';
      }
      
      if (this.animalSpeed) {
        this.animalSpeed.textContent = '';
      }
    }
  }
  
  // Update the reaction section of the inspector
  private updateReactionSection(animal: Animal) {
    const reactionsContainer = document.getElementById('reaction-entries');
    if (!reactionsContainer) return;
    
    // Clear previous entries
    reactionsContainer.innerHTML = '';
    
    if (animal.reactionLog.length === 0) {
      reactionsContainer.innerHTML = '<p>No reactions recorded yet.</p>';
      return;
    }
    
    // Get reference to emotion system for color lookup
    const emotionSystem = EmotionSystem.getInstance();
    
    // Add new entries from reaction log
    for (const reaction of animal.reactionLog.slice(0, 5)) { // Show most recent 5 reactions
      const entryDiv = document.createElement('div');
      entryDiv.className = 'reaction-entry';
      
      // Format timestamp
      const time = new Date(reaction.timestamp);
      const timeStr = `${time.getMinutes()}:${time.getSeconds().toString().padStart(2, '0')}`;
      
      // Get the color for this emotion type
      const emotionColor = emotionSystem.getColorForEmotion(reaction.emotionType);
      const colorHex = emotionColor ? emotionColor.toHex() : '#ffffff';
      
      entryDiv.innerHTML = `
        <div>
          <span class="reaction-type" style="color: ${colorHex}">${reaction.emotionType}</span>
          <span class="reaction-time">${timeStr}</span>
        </div>
        <div class="reaction-message">${reaction.message || ''}</div>
      `;
      
      reactionsContainer.appendChild(entryDiv);
    }
  }
  
  // Update the metabolism section of the inspector
  private updateMetabolismSection(animal: Animal) {
    const metabolismContainer = document.getElementById('metabolism-bars');
    if (!metabolismContainer) return;
    
    // Clear previous content
    metabolismContainer.innerHTML = '';
    
    // Create bars for hunger, thirst, energy
    const hungerBar = this.createMetabolismBar('Hunger', animal.metabolism.hunger, 'hunger-bar-fill');
    const thirstBar = this.createMetabolismBar('Thirst', animal.metabolism.thirst, 'thirst-bar-fill');
    const energyBar = this.createMetabolismBar('Energy', animal.metabolism.energy, 'energy-bar-fill');
    
    metabolismContainer.appendChild(hungerBar);
    metabolismContainer.appendChild(thirstBar);
    metabolismContainer.appendChild(energyBar);
  }
  
  // Helper to create metabolism bars
  private createMetabolismBar(label: string, value: number, fillClass: string): HTMLElement {
    const barContainer = document.createElement('div');
    barContainer.className = 'metabolism-bar';
    
    const barFill = document.createElement('div');
    barFill.className = `metabolism-bar-fill ${fillClass}`;
    
    // Calculate width (invert for hunger and thirst since high is bad)
    let displayValue = value;
    if (label === 'Hunger' || label === 'Thirst') {
      // Invert so that low hunger/thirst = full bar
      displayValue = 100 - value;
    }
    
    barFill.style.width = `${displayValue}%`;
    
    const barLabel = document.createElement('div');
    barLabel.className = 'metabolism-label';
    barLabel.textContent = `${label}: ${Math.round(value)}%`;
    
    barContainer.appendChild(barFill);
    barContainer.appendChild(barLabel);
    
    return barContainer;
  }

  // Check if we're currently following an entity
  public isFollowingEntity(): boolean {
    return this.followingEntity !== null;
  }

  // Move camera with arrow keys/WASD
  private panCamera(dx: number, dy: number) {
    this.isPanning = true;
    this.moveCamera(dx * this.keyboardPanSpeed * this.engine.deltaTime / 1000, 
                    dy * this.keyboardPanSpeed * this.engine.deltaTime / 1000);
  }

  // Move the camera by a delta amount, respecting world boundaries
  private moveCamera(dx: number, dy: number) {
    const newX = this.camera.pos.x + dx;
    const newY = this.camera.pos.y + dy;
    
    this.setConstrainedCameraPosition(newX, newY);
  }

  // Set camera position with constraints to keep within world bounds
  private setConstrainedCameraPosition(x: number, y: number) {
    // Calculate camera bounds to keep world visible
    const viewport = this.engine.screen.viewport;
    const halfWidth = viewport.width / 2;
    const halfHeight = viewport.height / 2;
    
    // Calculate the bounds with padding
    const minX = halfWidth;
    const maxX = WORLD_WIDTH - halfWidth;
    const minY = halfHeight;
    const maxY = WORLD_HEIGHT - halfHeight;
    
    // Clamp to ensure camera stays within world bounds
    const clampedX = Math.max(minX, Math.min(maxX, x));
    const clampedY = Math.max(minY, Math.min(maxY, y));
    
    // Apply the new position directly
    this.camera.pos = new ex.Vector(clampedX, clampedY);
  }

  // Focus camera on a specific grid cell immediately
  public focusOnCell(gridX: number, gridY: number) {
    const targetX = gridX * TILE_SIZE + TILE_SIZE / 2;
    const targetY = gridY * TILE_SIZE + TILE_SIZE / 2;
    
    // Stop following entity if we're focusing on a specific cell
    this.stopFollowingEntity();
    
    // Move camera to focus on target directly with constraints
    this.setConstrainedCameraPosition(targetX, targetY);
  }

  // Update camera position (called in game update loop)
  public update(engine: ex.Engine, delta: number) {
    // If following an entity, update camera position to follow it
    if (this.followingEntity) {
      // Update camera position
      const targetX = this.followingEntity.pos.x;
      const targetY = this.followingEntity.pos.y;
      
      // Move directly without smoothing for better performance
      this.setConstrainedCameraPosition(targetX, targetY);
      
      // Update inspect panel with the latest info
      if (Math.floor(engine.clock.elapsed() * 2) % 2 === 0) {
        // Update position (this is lightweight)
        this.updatePositionInPanel(this.followingEntity);
        
        // Update reaction and metabolism sections if it's an animal
        if (this.followingEntity instanceof Animal) {
          this.updateReactionSection(this.followingEntity as Animal);
          this.updateMetabolismSection(this.followingEntity as Animal);
        }
      }
    }
  }
  
  // Update just the position in the panel (performance optimization)
  private updatePositionInPanel(entity: Entity) {
    if (this.animalPosition) {
      this.animalPosition.textContent = `X: ${entity.gridX}, Y: ${entity.gridY}`;
    }
  }
}